import { BillingStatus, ProductLine, SubscriptionInfo, SubscriptionPlan } from '@/graphql/graphql';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

// Default subscription state (free plan, Viewer product line)
const defaultSubscription: SubscriptionInfo = {
  productLine: 'VIEWER' as ProductLine,
  plan: 'FREE' as SubscriptionPlan,
  isCustom: false,
  maxSeats: 1,
  seatsUsed: 0,
  billingStatus: 'NONE' as BillingStatus,
  collectionMethod: null,
  billingInterval: null,
  storageLimitBytes: 5_000_000_000,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  cancelAt: null,
  scheduledPlan: null,
  scheduledChangeAt: null,
  trialEndsAt: null,
};

// Main subscription atom
export const subscriptionAtom = atom<SubscriptionInfo>(defaultSubscription);

// "View as Viewer" switch (GIS-438), per browser. Read it through
// `useViewerPreview`, which also requires the PostHog flag.
export const viewerPreviewAtom = atomWithStorage<boolean>('gis.viewerPreview', false, undefined, { getOnInit: true });

// Derived atom for plan-based feature flags
export const subscriptionFeaturesAtom = atom((get) => {
  const subscription = get(subscriptionAtom);

  return {
    // Plan / product
    plan: subscription.plan,
    productLine: subscription.productLine,
    isCustom: subscription.isCustom,

    // Seat limits
    maxSeats: subscription.maxSeats,
    seatsUsed: subscription.seatsUsed,

    // Storage
    storageLimitBytes: subscription.storageLimitBytes,

    // Billing status checks
    isTrialing: subscription.billingStatus === 'TRIALING',
    isActive: subscription.billingStatus === 'ACTIVE',
    isPastDue: subscription.billingStatus === 'PAST_DUE',
    isCanceled: subscription.billingStatus === 'CANCELED',
    isPendingPayment: subscription.billingStatus === 'PENDING_PAYMENT',
    hasNoSubscription: subscription.billingStatus === 'NONE',

    // Trial
    trialEndsAt: subscription.trialEndsAt,

    // Cancellation status
    isCancelScheduled: subscription.cancelAtPeriodEnd,
    cancelAt: subscription.cancelAt,
  };
});

// Derived atom for billing status display
export const billingStatusAtom = atom((get) => {
  const subscription = get(subscriptionAtom);

  return {
    status: subscription.billingStatus,
    currentPeriodEnd: subscription.currentPeriodEnd,
    cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
    cancelAt: subscription.cancelAt,
    collectionMethod: subscription.collectionMethod,
    billingInterval: subscription.billingInterval,
  };
});

// Helper to format storage for display
export const formatStorageBytes = (bytes: number): string => {
  const gb = bytes / (1000 * 1000 * 1000);
  if (gb >= 1000) {
    return `${(gb / 1000).toFixed(1)} TB`;
  }
  if (gb >= 1) {
    return `${gb.toFixed(0)} GB`;
  }
  const mb = bytes / (1000 * 1000);
  if (mb >= 1) {
    return `${mb.toFixed(0)} MB`;
  }
  const kb = bytes / 1000;
  return `${kb.toFixed(0)} KB`;
};

// Plan display names
export const PLAN_DISPLAY_NAMES: Record<SubscriptionPlan, string> = {
  FREE: 'Free',
  STARTER: 'Starter',
  TEAM: 'Team',
  BUSINESS: 'Business',
  CUSTOM: 'Your plan', // fallback only — prefer planDisplayName() which names custom by product
};

// Product line display names
export const PRODUCT_DISPLAY_NAMES: Record<ProductLine, string> = {
  VIEWER: 'Viewer',
  PRO: 'Pro',
};

// Display name for a custom/negotiated plan, by product line.
export const CUSTOM_PLAN_DISPLAY_NAMES: Record<ProductLine, string> = {
  VIEWER: 'SkyGIS Viewer',
  PRO: 'SkyGIS Pro',
};

// Resolve the user-facing plan name for an org. Custom orgs are named by their
// product line (e.g. "SkyGIS Pro"); standard plans use PLAN_DISPLAY_NAMES.
export const planDisplayName = (sub: {
  plan: SubscriptionPlan;
  productLine: ProductLine;
  isCustom: boolean;
}): string => {
  if (sub.isCustom || sub.plan === 'CUSTOM') {
    return CUSTOM_PLAN_DISPLAY_NAMES[sub.productLine] ?? PLAN_DISPLAY_NAMES.CUSTOM;
  }
  return PLAN_DISPLAY_NAMES[sub.plan];
};

// Max projects per plan (null = unlimited)
export const PLAN_MAX_PROJECTS: Record<SubscriptionPlan, number | null> = {
  FREE: 5,
  STARTER: null,
  TEAM: null,
  BUSINESS: null,
  CUSTOM: null,
};

// Billing status display names
export const BILLING_STATUS_DISPLAY: Record<BillingStatus, { label: string; color: string }> = {
  NONE: { label: 'No Subscription', color: 'gray' },
  TRIALING: { label: 'Trial', color: 'blue' },
  ACTIVE: { label: 'Active', color: 'green' },
  PAST_DUE: { label: 'Past Due', color: 'red' },
  UNPAID: { label: 'Unpaid', color: 'red' },
  CANCELED: { label: 'Canceled', color: 'gray' },
  PENDING_PAYMENT: { label: 'Pending Payment', color: 'yellow' },
};
